/**
 * activePortfolio - app-wide shared "active portfolio" selection.
 *
 * usePortfolioData reads activePortfolioId from THIS context instead of its own
 * local state, so every page/tab shares one selection. The first usePortfolioData
 * to load bootstraps the id from settings.activePortfolioId (via bootstrappedRef);
 * after that the user's choice persists across route/tab changes within the session.
 */
import { createContext, useContext, useRef, useState } from 'react';
import type { Dispatch, ReactNode, SetStateAction, MutableRefObject } from 'react';

interface ActivePortfolioCtx {
  activePortfolioId: string;
  setActivePortfolioId: Dispatch<SetStateAction<string>>;
  /** Flips true once any usePortfolioData has seeded the id from settings. */
  bootstrappedRef: MutableRefObject<boolean>;
}

const Ctx = createContext<ActivePortfolioCtx | null>(null);

export function ActivePortfolioProvider({ children }: { children: ReactNode }) {
  const [activePortfolioId, setActivePortfolioId] = useState<string>('');
  const bootstrappedRef = useRef(false);
  return (
    <Ctx.Provider value={{ activePortfolioId, setActivePortfolioId, bootstrappedRef }}>
      {children}
    </Ctx.Provider>
  );
}

export function useActivePortfolio(): ActivePortfolioCtx {
  const ctx = useContext(Ctx);
  // Per-instance fallback for consumers rendered WITHOUT the provider (unit
  // tests, isolated usage): behaves exactly like the old local state, just not
  // shared. Hooks run unconditionally so hook order stays stable.
  const [localId, setLocalId] = useState('');
  const localBootstrapped = useRef(false);
  if (ctx) return ctx;
  return { activePortfolioId: localId, setActivePortfolioId: setLocalId, bootstrappedRef: localBootstrapped };
}
